// Curated list of countries used as "Country of Origin" for industrial
// listings on IndusFact. Kept intentionally small (~30 entries) so the
// dropdown stays scannable and the data stays filterable.
// No free-text — if a country is missing, add it here so filters keep working.
//
// Order: India first (most common for our audience), then top industrial
// origin countries by relevance to Indian buyers of used machinery.

export const COUNTRIES_OF_ORIGIN = [
  'India',
  'Japan',
  'Germany',
  'USA',
  'China',
  'South Korea',
  'Taiwan',
  'Italy',
  'United Kingdom',
  'France',
  'Switzerland',
  'Sweden',
  'Spain',
  'Turkey',
  'Austria',
  'Netherlands',
  'Belgium',
  'Czech Republic',
  'Poland',
  'Brazil',
  'Mexico',
  'Canada',
  'Australia',
  'Singapore',
  'Malaysia',
  'Thailand',
  'Vietnam',
  'UAE',
  'Israel',
  'Russia',
  'Finland',
  'Denmark',
  'Portugal',
  'South Africa',
];

// Dial code + expected national-number length per country. Length is checked
// as a plain digit count (min/max) — deliberately no phone-number library, so
// the rule is "right prefix, plausible length", not carrier-level validation.
export const COUNTRY_DIAL = {
  India: { code: '+91', min: 10, max: 10 },
  Japan: { code: '+81', min: 9, max: 10 },
  Germany: { code: '+49', min: 6, max: 13 },
  USA: { code: '+1', min: 10, max: 10 },
  China: { code: '+86', min: 10, max: 11 },
  'South Korea': { code: '+82', min: 9, max: 10 },
  Taiwan: { code: '+886', min: 8, max: 9 },
  Italy: { code: '+39', min: 6, max: 11 },
  'United Kingdom': { code: '+44', min: 9, max: 10 },
  France: { code: '+33', min: 9, max: 9 },
  Switzerland: { code: '+41', min: 9, max: 9 },
  Sweden: { code: '+46', min: 7, max: 9 },
  Spain: { code: '+34', min: 9, max: 9 },
  Turkey: { code: '+90', min: 10, max: 10 },
  Austria: { code: '+43', min: 7, max: 13 },
  Netherlands: { code: '+31', min: 9, max: 9 },
  Belgium: { code: '+32', min: 8, max: 9 },
  'Czech Republic': { code: '+420', min: 9, max: 9 },
  Poland: { code: '+48', min: 9, max: 9 },
  Brazil: { code: '+55', min: 10, max: 11 },
  Mexico: { code: '+52', min: 10, max: 10 },
  Canada: { code: '+1', min: 10, max: 10 },
  Australia: { code: '+61', min: 9, max: 9 },
  Singapore: { code: '+65', min: 8, max: 8 },
  Malaysia: { code: '+60', min: 9, max: 10 },
  Thailand: { code: '+66', min: 9, max: 9 },
  Vietnam: { code: '+84', min: 9, max: 10 },
  UAE: { code: '+971', min: 9, max: 9 },
  Israel: { code: '+972', min: 9, max: 9 },
  Russia: { code: '+7', min: 10, max: 10 },
  Finland: { code: '+358', min: 9, max: 10 },
  Denmark: { code: '+45', min: 8, max: 8 },
  Portugal: { code: '+351', min: 9, max: 9 },
  'South Africa': { code: '+27', min: 9, max: 9 },
};

export const DEFAULT_COUNTRY = 'India';

// Longest prefix first so +91 never wins over +911-style longer codes, and
// +1 never swallows +1-prefixed longer codes.
const DIAL_ENTRIES = Object.entries(COUNTRY_DIAL)
  .map(([country, d]) => ({ country, ...d }))
  .sort((a, b) => b.code.length - a.code.length);

export const dialCountries = () => COUNTRIES_OF_ORIGIN.filter(c => COUNTRY_DIAL[c]);

/** First country owning a dial code (+1 resolves to USA, Canada shares it). */
export const countryForDial = (code) =>
  COUNTRIES_OF_ORIGIN.find(c => COUNTRY_DIAL[c]?.code === code) || null;

/** "+919876543210" → { dial: '+91', national: '9876543210', country: 'India' } */
export const splitMobile = (value) => {
  const raw = String(value || '').replace(/[\s\-().]/g, '');
  if (!raw.startsWith('+')) {
    return { dial: '', national: raw.replace(/\D/g, ''), country: null };
  }
  const match = DIAL_ENTRIES.find(e => raw.startsWith(e.code));
  if (!match) return { dial: '', national: raw.replace(/\D/g, ''), country: null };
  return {
    dial: match.code,
    national: raw.slice(match.code.length).replace(/\D/g, ''),
    country: countryForDial(match.code),
  };
};

/**
 * Prefix + length check only. Returns { ok, message } so callers can push the
 * message straight into their existing toast/error list.
 */
export const validateMobile = (value, { required = false } = {}) => {
  const v = String(value || '').trim();
  if (!v) return required ? { ok: false, message: 'Mobile number is required' } : { ok: true };
  if (!v.startsWith('+')) return { ok: false, message: 'Mobile number must start with a country code' };
  const { dial, national, country } = splitMobile(v);
  if (!dial || !country) return { ok: false, message: `Unsupported country code in "${v}"` };
  const rule = COUNTRY_DIAL[country];
  if (national.length < rule.min || national.length > rule.max) {
    const expect = rule.min === rule.max ? `${rule.min} digits` : `${rule.min}–${rule.max} digits`;
    return { ok: false, message: `A ${dial} number needs ${expect} after the country code` };
  }
  return { ok: true };
};

// Country → emoji flag map (nice-to-have for the ListingDetail specs +
// card badge). Only the countries above are mapped; anything else falls
// back to no flag. Kept as a helper — not a component prop.
export const COUNTRY_FLAG = {
  India: 'IN', Japan: 'JP', Germany: 'DE', USA: 'US', China: 'CN',
  'South Korea': 'KR', Taiwan: 'TW', Italy: 'IT', 'United Kingdom': 'GB',
  France: 'FR', Switzerland: 'CH', Sweden: 'SE', Spain: 'ES', Turkey: 'TR',
  Austria: 'AT', Netherlands: 'NL', Belgium: 'BE', 'Czech Republic': 'CZ',
  Poland: 'PL', Brazil: 'BR', Mexico: 'MX', Canada: 'CA', Australia: 'AU',
  Singapore: 'SG', Malaysia: 'MY', Thailand: 'TH', Vietnam: 'VN',
  UAE: 'AE', Israel: 'IL', Russia: 'RU', Finland: 'FI', Denmark: 'DK',
  Portugal: 'PT', 'South Africa': 'ZA',
};
